import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Nfc, QrCode } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';
import {
  formatPersonDates,
  getMemorialPublicUrl,
  isMemorialPublished,
  personDisplayName,
} from '../../utils/memorial';
import { memorialQrPublicUrl } from '../../utils/publicAppUrl';
import { personHref } from '../../utils/prettyArchiveLinks';
import { storyHasVoice } from '../../utils/storyDisplay';
import {
  MEMORIAL_KEEPSAKES_NFC_NOTE,
  MEMORIAL_KEEPSAKES_PREVIEW_DISCLAIMER,
  MEMORIAL_PHOTO_LINK_ACTIVATION_NOTE,
  MEMORIAL_PHOTO_LINK_NAME,
} from '../../content/memorialKeepsakesContent';
import {
  AnniversaryRemembrancePreviewBlock,
  DigitalExperiencePreviewSection,
  FeaturedVoiceMemorySection,
  FrameModePreviewNote,
  LegacyLinkCertificatePreview,
  MemoryOfTheDayPreviewBlock,
  PublicPrivateNfcConceptCard,
} from './MemorialPhotoLinkPreviewSections';
import { KeepsakeFrameMockup, ModeBadge, QrActivationPlaceholder } from './KeepsakeFrameMockup';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();

export function MemorialKeepsakePreviewDialog({
  open,
  onOpenChange,
  product,
  person,
  memorial,
  publicUrl,
  qrInfo,
  builderConfig,
}) {
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [featuredStory, setFeaturedStory] = useState(null);
  const published = isMemorialPublished(memorial);
  const name = personDisplayName(person);
  const dates = formatPersonDates(person);
  const memorialTitle = memorial?.title?.trim() || `${name} — Memorial`;
  const redirectUrl = qrInfo?.redirect_url || memorialQrPublicUrl(qrInfo?.short_code);
  const memorialLink = getMemorialPublicUrl(publicUrl, memorial?.slug);
  const addVoiceHref = person ? `${personHref(person)}?view=stories&focus=voice` : null;

  const frameStyle = builderConfig?.frameStyle;
  const layout = builderConfig?.layout;
  const previewImageUrl = builderConfig?.previewImageUrl ?? null;
  const orientation = builderConfig?.orientation ?? 'portrait';
  const imagePosition = builderConfig?.imagePosition;
  const imageEdits = builderConfig?.imageEdits;

  useEffect(() => {
    if (!open) {
      if (qrPreviewUrl) URL.revokeObjectURL(qrPreviewUrl);
      setQrPreviewUrl(null);
      setFeaturedStory(null);
      return undefined;
    }
    if (!published || !memorial?.id) return undefined;

    let cancelled = false;
    setLoadingQr(true);
    axios
      .get(`${API}/memorials-api/${memorial.id}/qr`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        setQrPreviewUrl(URL.createObjectURL(res.data));
      })
      .catch(() => {
        if (!cancelled) setQrPreviewUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingQr(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, published, memorial?.id]);

  useEffect(() => {
    if (!open || !person?.id) {
      setFeaturedStory(null);
      return undefined;
    }

    let cancelled = false;
    axios
      .get(`${API}/individuals/${person.id}/stories`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setFeaturedStory(list.find(storyHasVoice) || null);
      })
      .catch(() => {
        if (!cancelled) setFeaturedStory(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, person?.id]);

  useEffect(
    () => () => {
      if (qrPreviewUrl) URL.revokeObjectURL(qrPreviewUrl);
    },
    [qrPreviewUrl],
  );

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-md overflow-y-auto border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] sm:max-w-2xl"
        data-testid="memorial-keepsake-preview"
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
              {MEMORIAL_PHOTO_LINK_NAME}
            </p>
            <ModeBadge published={published} />
          </div>
          <DialogTitle className="font-serif text-xl font-light text-[#1c1917]">
            Preview — {product.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5c534a]">
            {MEMORIAL_KEEPSAKES_PREVIEW_DISCLAIMER}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
          <KeepsakeFrameMockup
            imageUrl={previewImageUrl}
            name={name}
            dates={dates}
            memorialTitle={memorialTitle}
            frameStyle={frameStyle}
            layout={layout}
            product={product}
            orientation={orientation}
            imagePosition={imagePosition}
            imageEdits={imageEdits}
            published={published}
            size="lg"
          />

          <div className="min-w-0 flex-1 space-y-2 text-sm">
            <p className="font-medium text-[#1c1917]">{memorialTitle}</p>
            <p className="text-xs text-[#5c534a]">
              {product.variantOf} · {product.size}
              {frameStyle ? ` · ${frameStyle.label}` : ''}
            </p>
            {layout ? <p className="text-xs text-[#5c534a]/90">{layout.label}</p> : null}
            {published && memorialLink ? (
              <p className="break-all text-[11px] text-muted-foreground">Live destination: {memorialLink}</p>
            ) : (
              <p className="text-[11px] text-[#8b7355]">{MEMORIAL_PHOTO_LINK_ACTIVATION_NOTE}</p>
            )}
          </div>
        </div>

        <LegacyLinkCertificatePreview
          name={name}
          memorialTitle={memorialTitle}
          redirectUrl={published ? redirectUrl : null}
          qrPreviewUrl={qrPreviewUrl}
          loadingQr={loadingQr}
          published={published}
          publishedAt={memorial?.published_at}
        />

        <div className="space-y-3 rounded-xl border border-[hsl(38_14%_86%)]/70 bg-white/80 p-4">
          <div className="flex items-start gap-2 text-sm text-[#3d3833]">
            <QrCode className="mt-0.5 h-4 w-4 shrink-0 text-[#8b7355]" aria-hidden />
            <div>
              <p className="font-medium">QR &amp; NFC link</p>
              {published ? (
                <>
                  {loadingQr ? (
                    <Loader2 className="mt-2 h-6 w-6 animate-spin text-muted-foreground" />
                  ) : qrPreviewUrl ? (
                    <img
                      src={qrPreviewUrl}
                      alt={`QR code linking to ${name} memorial`}
                      className="mt-2 h-28 w-28 rounded border border-[hsl(38_14%_86%)] bg-white p-1.5"
                    />
                  ) : (
                    <QrActivationPlaceholder published />
                  )}
                  {redirectUrl ? (
                    <p className="mt-2 break-all font-mono text-[10px] text-muted-foreground">{redirectUrl}</p>
                  ) : null}
                </>
              ) : (
                <>
                  <QrActivationPlaceholder published={false} className="mt-2" />
                  <p className="mt-2 text-xs leading-relaxed text-[#5c534a]">
                    Preview shows a placeholder. Your dynamic /q/:shortCode link is assigned when the memorial is
                    published and activated.
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2 border-t border-[hsl(38_14%_88%)] pt-3 text-sm text-[#3d3833]">
            <Nfc className="mt-0.5 h-4 w-4 shrink-0 text-[#8b7355]" aria-hidden />
            <p className="text-xs leading-relaxed text-[#5c534a]">{MEMORIAL_KEEPSAKES_NFC_NOTE}</p>
          </div>
        </div>

        <FeaturedVoiceMemorySection
          featuredStory={featuredStory}
          backendBase={BACKEND}
          addVoiceHref={addVoiceHref}
        />

        <PublicPrivateNfcConceptCard />

        <DigitalExperiencePreviewSection />

        <div className="grid gap-2 sm:grid-cols-2">
          <MemoryOfTheDayPreviewBlock />
          <AnniversaryRemembrancePreviewBlock />
        </div>

        <FrameModePreviewNote />
      </DialogContent>
    </Dialog>
  );
}

export default MemorialKeepsakePreviewDialog;
